import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Play, Download, Bookmark, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { ReportFilters, REPORT_TYPES, type ReportFilterValues } from "./ReportFilters";
import { ReportTable } from "./ReportTable";
import { DemographicsReport } from "./DemographicsReport";
import { ActivitiesReport } from "./ActivitiesReport";
import { SavedQueries } from "./SavedQueries";

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

function exportCsv(data: Record<string, unknown>[], filename: string) {
  if (data.length === 0) return;
  const first = data[0];
  if (!first) return;
  const keys = Object.keys(first);
  const header = keys.join(",");
  const rows = data.map((row) =>
    keys
      .map((k) => {
        const val = row[k];
        if (val == null) return "";
        const str = String(val);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      })
      .join(","),
  );
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Flatten report data for table/CSV
// ---------------------------------------------------------------------------

function flattenCountRows(data: { label: string; count: number }[]): Record<string, unknown>[] {
  return data.map((r) => ({ Category: r.label, Count: r.count }));
}

function getTableData(reportType: string, data: unknown): {
  columns: { key: string; label: string }[];
  rows: Record<string, unknown>[];
} {
  if (!data) return { columns: [], rows: [] };

  const d = data as Record<string, unknown>;

  switch (reportType) {
    case "client-list": {
      const clients = (d.clients ?? []) as Record<string, unknown>[];
      return {
        columns: [
          { key: "id", label: "ID" },
          { key: "last_name", label: "Last Name" },
          { key: "first_name", label: "First Name" },
          { key: "person_type", label: "Type" },
          { key: "intake_date", label: "Intake Date" },
          { key: "exit_date", label: "Exit Date" },
          { key: "gender", label: "Gender" },
          { key: "address_city", label: "City" },
          { key: "address_state", label: "State" },
        ],
        rows: clients,
      };
    }

    case "departures": {
      const departures = (d.departures ?? []) as Record<string, unknown>[];
      return {
        columns: [
          { key: "id", label: "ID" },
          { key: "last_name", label: "Last Name" },
          { key: "first_name", label: "First Name" },
          { key: "person_type", label: "Type" },
          { key: "intake_date", label: "Intake" },
          { key: "exit_date", label: "Exit" },
        ],
        rows: departures,
      };
    }

    case "outcomes": {
      const outcomes = (d.outcomes ?? []) as Record<string, unknown>[];
      return {
        columns: [
          { key: "id", label: "ID" },
          { key: "category_name", label: "Category" },
          { key: "outcome_type", label: "Type" },
          { key: "outcome_value", label: "Value" },
          { key: "outcome_date", label: "Date" },
        ],
        rows: outcomes,
      };
    }

    case "bednights": {
      const byDate = (d.byDate ?? []) as Record<string, unknown>[];
      return {
        columns: [
          { key: "date", label: "Date" },
          { key: "occupied", label: "Occupied Beds" },
          { key: "capacity", label: "Total Capacity" },
        ],
        rows: byDate,
      };
    }

    case "turnaways": {
      const byReason = (d.byReason ?? []) as { label: string; count: number }[];
      return {
        columns: [
          { key: "Category", label: "Reason" },
          { key: "Count", label: "Count" },
        ],
        rows: flattenCountRows(byReason),
      };
    }

    case "meals": {
      const byType = (d.byType ?? []) as { label: string; count: number }[];
      return {
        columns: [
          { key: "Category", label: "Meal Type" },
          { key: "Count", label: "Count" },
        ],
        rows: flattenCountRows(byType),
      };
    }

    case "referrals": {
      const bySource = (d.bySource ?? []) as { label: string; count: number }[];
      return {
        columns: [
          { key: "Category", label: "Source" },
          { key: "Count", label: "Count" },
        ],
        rows: flattenCountRows(bySource),
      };
    }

    case "legal": {
      const poStatus = (d.protectionOrdersByStatus ?? []) as { label: string; count: number }[];
      return {
        columns: [
          { key: "Category", label: "Protection Order Status" },
          { key: "Count", label: "Count" },
        ],
        rows: flattenCountRows(poStatus),
      };
    }

    default:
      return { columns: [], rows: [] };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const reportTabs = [
  { id: "run", label: "Run Report" },
  { id: "saved", label: "Saved Queries" },
];

export function ReportRunner() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("run");
  const [filters, setFilters] = useState<ReportFilterValues>({
    reportType: "demographics",
    dateFrom: "",
    dateTo: "",
    personType: "",
  });
  const [reportData, setReportData] = useState<unknown>(null);
  const [lastReportType, setLastReportType] = useState<string>("");
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState("");

  // Run report mutation
  const runMutation = useMutation({
    mutationFn: async (params: ReportFilterValues) => {
      const body: Record<string, unknown> = {};
      if (params.dateFrom) body.dateFrom = params.dateFrom;
      if (params.dateTo) body.dateTo = params.dateTo;
      if (params.personType) {
        body.filters = { personType: params.personType };
      }
      const res = await api.post<{ data: unknown }>(
        `/reports/${params.reportType}`,
        body,
      );
      return res.data;
    },
    onSuccess: (data) => {
      setReportData(data);
      setLastReportType(filters.reportType);
    },
  });

  // Save query mutation
  const saveMutation = useMutation({
    mutationFn: async (name: string) => {
      return api.post("/reports/saved-queries", {
        name,
        queryConfig: {
          reportType: filters.reportType,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          filters: filters.personType
            ? { personType: filters.personType }
            : undefined,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
      setShowSaveForm(false);
      setSaveName("");
    },
  });

  function handleRun() {
    if (!filters.reportType) return;
    runMutation.mutate(filters);
  }

  function handleExportCsv() {
    if (!reportData || !lastReportType) return;
    const { rows } = getTableData(lastReportType, reportData);
    if (rows.length > 0) {
      exportCsv(rows, `${lastReportType}-report`);
      return;
    }
    // For demographics/activities, export a flat summary
    const d = reportData as Record<string, unknown>;
    const allSections = Object.entries(d).filter(
      ([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === "object" && "label" in v[0],
    );
    const flatRows: Record<string, unknown>[] = [];
    for (const [key, arr] of allSections) {
      for (const item of arr as { label: string; count: number }[]) {
        flatRows.push({ Section: key, Category: item.label, Count: item.count });
      }
    }
    if (flatRows.length > 0) {
      exportCsv(flatRows, `${lastReportType}-report`);
    }
  }

  const handleRunSavedQuery = useCallback(
    (config: {
      reportType: string;
      dateFrom?: string;
      dateTo?: string;
      filters?: Record<string, unknown>;
    }) => {
      const newFilters: ReportFilterValues = {
        reportType: config.reportType,
        dateFrom: config.dateFrom ?? "",
        dateTo: config.dateTo ?? "",
        personType: (config.filters?.personType as string) ?? "",
      };
      setFilters(newFilters);
      setActiveTab("run");
      runMutation.mutate(newFilters);
    },
    [runMutation],
  );

  const tableData = getTableData(lastReportType, reportData);
  const reportLabel =
    REPORT_TYPES.find((t) => t.value === lastReportType)?.label ?? lastReportType;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reports</h1>
          <p className="text-sm text-muted-foreground">
            Generate and export reports from your case management data
          </p>
        </div>
      </div>

      <Tabs tabs={reportTabs} activeTab={activeTab} onChange={setActiveTab} />

      <TabPanel id="run" activeTab={activeTab}>
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 space-y-4">
              <ReportFilters values={filters} onChange={setFilters} />
              <div className="flex items-center gap-3">
                <Button
                  className="gap-2"
                  onClick={handleRun}
                  disabled={!filters.reportType || runMutation.isPending}
                >
                  {runMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Run Report
                </Button>
                {reportData != null && (
                  <>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={handleExportCsv}
                    >
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => setShowSaveForm(!showSaveForm)}
                    >
                      <Bookmark className="h-4 w-4" />
                      Save Query
                    </Button>
                  </>
                )}
              </div>

              {showSaveForm && (
                <div className="flex items-end gap-3 rounded-md border p-3 bg-muted/30">
                  <div className="flex-1">
                    <Input
                      label="Query Name"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="e.g. Monthly Demographics Q1"
                      required
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => saveName && saveMutation.mutate(saveName)}
                    loading={saveMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSaveForm(false);
                      setSaveName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {runMutation.isError && (
            <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
              {(runMutation.error as Error)?.message ?? "Failed to run report"}
            </div>
          )}

          {reportData != null && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">
                {reportLabel} Results
              </h2>

              {/* Specialized displays */}
              {lastReportType === "demographics" && (
                <DemographicsReport data={reportData as any} />
              )}

              {lastReportType === "activities" && (
                <ActivitiesReport data={reportData as any} />
              )}

              {/* Summary stats for certain report types */}
              {lastReportType === "bednights" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  {[
                    { label: "Total Bednights", value: (reportData as any).totalBednights },
                    { label: "Total Beds", value: (reportData as any).totalBeds },
                    { label: "Avg Occupancy", value: (reportData as any).averageOccupancy },
                    { label: "Utilization %", value: `${(reportData as any).capacityUtilization}%` },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg border bg-primary/5 p-4">
                      <p className="text-sm text-muted-foreground">{s.label}</p>
                      <p className="text-2xl font-bold text-primary">{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {lastReportType === "departures" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border bg-primary/5 p-4">
                    <p className="text-sm text-muted-foreground">Total Departures</p>
                    <p className="text-2xl font-bold text-primary">
                      {(reportData as any).totalDepartures}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-primary/5 p-4">
                    <p className="text-sm text-muted-foreground">Avg Length of Stay</p>
                    <p className="text-2xl font-bold text-primary">
                      {(reportData as any).avgLengthOfStayDays} days
                    </p>
                  </div>
                </div>
              )}

              {lastReportType === "legal" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[
                    { label: "Legal Cases", value: (reportData as any).totalLegalCases },
                    { label: "Protection Orders", value: (reportData as any).totalProtectionOrders },
                    { label: "Court Advocacy", value: (reportData as any).totalCourtAdvocacy },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg border bg-primary/5 p-4">
                      <p className="text-sm text-muted-foreground">{s.label}</p>
                      <p className="text-2xl font-bold text-primary">{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {lastReportType === "turnaways" && (
                <div className="rounded-lg border bg-primary/5 p-4">
                  <p className="text-sm text-muted-foreground">Total Turnaways</p>
                  <p className="text-2xl font-bold text-primary">
                    {(reportData as any).totalTurnaways}
                  </p>
                </div>
              )}

              {lastReportType === "meals" && (
                <div className="rounded-lg border bg-primary/5 p-4">
                  <p className="text-sm text-muted-foreground">Total Meals Served</p>
                  <p className="text-2xl font-bold text-primary">
                    {(reportData as any).totalMeals}
                  </p>
                </div>
              )}

              {lastReportType === "outcomes" && (
                <div className="rounded-lg border bg-primary/5 p-4">
                  <p className="text-sm text-muted-foreground">Total Outcomes</p>
                  <p className="text-2xl font-bold text-primary">
                    {(reportData as any).totalOutcomes}
                  </p>
                </div>
              )}

              {lastReportType === "referrals" && (
                <div className="rounded-lg border bg-primary/5 p-4">
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-2xl font-bold text-primary">
                    {(reportData as any).totalReferrals}
                  </p>
                </div>
              )}

              {/* Table display for applicable report types */}
              {tableData.rows.length > 0 && (
                <ReportTable columns={tableData.columns} rows={tableData.rows} />
              )}
            </div>
          )}
        </div>
      </TabPanel>

      <TabPanel id="saved" activeTab={activeTab}>
        <SavedQueries onRunQuery={handleRunSavedQuery} />
      </TabPanel>
    </div>
  );
}
